import { Call } from 'src/models';
import { Button, Dropdown, MenuProps } from 'antd';
import {
  DotsVertical,
  Archive,
  ReverseLeft,
} from '@insight-health-ai/roseate/icons';
import { Modal, toast } from '@insight-health-ai/roseate/components';
import {
  CheckOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  useMarkCallProcessed,
  useMarkCallAsRead,
  useDeleteCall,
} from 'src/hooks/call';
import { usePermissionEntitlements } from '@frontegg/react';

type ActionProps = {
  call: Call;
};

export const Actions = ({ call }: ActionProps) => {
  const actions = [] as Exclude<MenuProps['items'], undefined>;
  const { isEntitled: hasCallDeleteAccess } =
    usePermissionEntitlements('calls.delete');

  const { mutateAsync: markCallProcessed, isPending: isMarkingCallProcessed } =
    useMarkCallProcessed();

  const { mutateAsync: markCallRead, isPending: isMarkingCallRead } =
    useMarkCallAsRead();

  const { mutateAsync: deleteCall, isPending: isDeletingCall } =
    useDeleteCall();

  const processed =
    (call.status?.call_status_key || '').toLowerCase() === 'processed';

  actions.push({
    icon: isMarkingCallProcessed ? (
      <LoadingOutlined />
    ) : processed ? (
      <ReverseLeft />
    ) : (
      <CheckOutlined />
    ),
    key: 'mark-as-processed',
    label: processed
      ? `Revert to ${call.processed_from_status?.call_status_name || 'unprocessed'}`
      : 'Mark as processed',
    disabled: isMarkingCallProcessed,
    onClick: () => {
      markCallProcessed({
        callId: call.call_id,
        processed: !processed,
      }).catch(() => {
        toast.error('Failed to change the processed status');
      });
    },
  });

  actions.push({
    icon: isMarkingCallRead ? (
      <LoadingOutlined />
    ) : call.is_read ? (
      <EyeOutlined />
    ) : (
      <EyeInvisibleOutlined />
    ),
    disabled: isMarkingCallRead,
    key: 'mark-as-read',
    label: `Mark as ${call.is_read ? 'unread' : 'read'}`,
    onClick: async () => {
      await markCallRead({
        callId: call.call_id,
        isRead: !call.is_read,
      }).catch(() => {
        toast.error('Failed to change the read status');
      });
    },
  });

  if (hasCallDeleteAccess) {
    actions.push({
      icon: isDeletingCall ? <LoadingOutlined /> : <Archive />,
      danger: true,
      key: 'delete',
      label: 'Archive',
      disabled: isDeletingCall,
      onClick: async () => {
        Modal.confirm({
          appearance: 'error',
          title: 'Archive Call',
          content: 'Are you sure you want to archive this call?',
          primaryAction: {
            label: 'Archive',
            onClick: async () => {
              await deleteCall({ callId: call.call_id }).catch((error) => {
                toast.error('Failed to archive the call');
                throw error;
              });
              toast.success('Call archived successfully');
            },
          },
        });
      },
    });
  }

  return (
    <Dropdown
      trigger={['click']}
      menu={{
        items: actions,
      }}
    >
      <Button type="text" icon={<DotsVertical />} />
    </Dropdown>
  );
};
